import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Check } from "lucide-react";
import { ApiError } from "@finora/api-client";
import { Button } from "@finora/ui";
import { api } from "../../../lib/api";

export interface SelectedPackage {
  id: string;
  key: string;
  name: string;
  price: string;
  priceGross: string;
  trialEnabled: boolean;
  trialDurationDays: number | null;
  includedUsers: number;
  includedModules: Array<{ id: string; key: string; name: string; description: string | null }>;
  isInstantTrial: boolean;
}

interface Props {
  selected: SelectedPackage | null;
  onSelected: (pkg: SelectedPackage) => void;
  onNext: () => void;
  onBack: () => void;
}

/**
 * Onboarding, korak "Paket" - lista STVARNIH paketa iz GET /company/subscription
 * (isti podaci kao "Moj paket" stranica). Vidi razgovor "Samo Starter je
 * instant, ostali idu na fakturu": paket koji je trialEnabled I ima cenu 0
 * se aktivira ODMAH (POST /company/subscription/start-trial); svaki drugi
 * paket generiše ponudu za plaćanje (isti tok kao MyPackagePage), a
 * onboarding samo obaveštava da će paket biti aktivan po plaćanju.
 */
export function PackageStep({ selected, onSelected, onNext, onBack }: Props) {
  const [pickedId, setPickedId] = useState<string | null>(selected?.id ?? null);

  const { data, isLoading } = useQuery({
    queryKey: ["subscription-packages-onboarding"],
    queryFn: () => api.getMyPackage(),
  });

  const activate = useMutation({
    mutationFn: async () => {
      const pkg = data?.packages.find((p) => p.id === pickedId);
      if (!pkg) throw new Error("Izaberite paket.");
      const isInstantTrial = pkg.trialEnabled && Number(pkg.price) === 0;
      if (isInstantTrial) {
        await api.startTrial(pkg.id);
      } else {
        await api.changePackage(pkg.id);
      }
      onSelected({ ...pkg, isInstantTrial });
    },
    onSuccess: () => onNext(),
  });

  return (
    <div className="rounded-xl border border-border bg-surface p-8 shadow-subtle">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-brand">Izbor paketa</p>
      <h1 className="mb-1 text-2xl font-bold text-slate-900">Izaberite paket koji odgovara vašem poslovanju</h1>
      <p className="mb-6 text-sm text-slate-500">
        Besplatan paket se aktivira odmah. Plaćeni paket generiše fakturu - aktivira se po naplati.
      </p>

      {isLoading ? (
        <p className="text-sm text-slate-400">Učitavanje paketa...</p>
      ) : (
        <div className="grid gap-4 md:grid-cols-3">
          {data?.packages.map((pkg) => {
            const isPicked = pickedId === pkg.id;
            const isFree = pkg.trialEnabled && Number(pkg.price) === 0;
            return (
              <button
                key={pkg.id}
                type="button"
                onClick={() => setPickedId(pkg.id)}
                className={`flex flex-col rounded-xl border p-5 text-left transition-colors ${
                  isPicked ? "border-brand bg-brand-light" : "border-border hover:border-brand-border"
                }`}
              >
                {pkg.isCurrent && (
                  <span className="mb-2 inline-block w-fit rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">
                    Trenutni paket
                  </span>
                )}
                <p className="text-base font-semibold text-slate-900">{pkg.name}</p>
                <p className="mt-1 text-xl font-bold text-slate-900">
                  {Number(pkg.priceGross) === 0
                    ? "0 RSD"
                    : `${Number(pkg.priceGross).toLocaleString("sr-RS")} RSD`}
                  <span className="text-xs font-normal text-slate-400">
                    {" "}
                    {isFree && pkg.trialDurationDays ? `/ ${pkg.trialDurationDays} dana probe` : "/ mesečno"}
                  </span>
                </p>
                {!isFree && (
                  <p className="text-xs text-slate-400">
                    sa PDV-om ({Number(pkg.price).toLocaleString("sr-RS")} RSD bez PDV-a)
                  </p>
                )}
                <ul className="mt-3 space-y-1 text-xs text-slate-500">
                  <li className="flex items-center gap-1.5">
                    <Check className="h-3.5 w-3.5 text-emerald-600" /> Do {pkg.includedUsers} korisnika
                  </li>
                  <li className="flex items-center gap-1.5">
                    <Check className="h-3.5 w-3.5 text-emerald-600" /> {pkg.includedModules.length} modula
                  </li>
                  {isFree && (
                    <li className="flex items-center gap-1.5">
                      <Check className="h-3.5 w-3.5 text-emerald-600" /> Aktivira se odmah
                    </li>
                  )}
                </ul>
                <span
                  className={`mt-4 rounded-lg px-3 py-2 text-center text-sm font-medium ${
                    isPicked ? "bg-brand text-white" : "border border-border text-slate-700"
                  }`}
                >
                  {isPicked ? "Izabran paket" : "Izaberi paket"}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {activate.isError && (
        <p className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
          {activate.error instanceof ApiError ? activate.error.response.message : "Aktivacija paketa nije uspela."}
        </p>
      )}

      <div className="mt-6 flex justify-between border-t border-border pt-6">
        <Button variant="secondary" onClick={onBack}>
          ← Nazad
        </Button>
        <Button onClick={() => activate.mutate()} disabled={!pickedId || activate.isPending}>
          {activate.isPending ? "Aktiviram..." : "Nastavi →"}
        </Button>
      </div>
    </div>
  );
}
